// Helpers shared by the runtime renderer.

import { richPlainText } from '../richContent'
import type { ColumnDef, SqlModel, TransactionModel } from './types'
import type { ErrorEnvelope, ValidationErrorItem } from './contracts'

// Token convention shared by audit-defaults and project-level variables:
// a leading colon marks "this is a token, resolve at save time". The
// bare name after the colon is either one of the well-known audit names
// (handled by the audit-defaults stamper) or a project-defined variable.

export const TOKEN_PREFIX = ':'

// Mirror of backend audit_defaults.ALL_TOKENS.
export const AUDIT_TOKENS = [
  ':audit_add_date',
  ':audit_chg_date',
  ':audit_add_user',
  ':audit_chg_user',
  ':audit_add_terminal',
  ':audit_chg_terminal',
] as const

export type AuditToken = (typeof AUDIT_TOKENS)[number]

export const AUDIT_TOKEN_LABELS: Record<AuditToken, string> = {
  ':audit_add_date': 'Add Date (set on insert)',
  ':audit_chg_date': 'Change Date (set on insert + update)',
  ':audit_add_user': 'Add User (set on insert)',
  ':audit_chg_user': 'Change User (set on insert + update)',
  ':audit_add_terminal': 'Add Terminal/IP (set on insert)',
  ':audit_chg_terminal': 'Change Terminal/IP (set on insert + update)',
}

// Calendar tokens — pre-fill date/datetime columns from the server
// clock. Mirror of backend ``calendar_defaults.CALENDAR_TOKENS``. Unlike
// audit stamps these behave like defaults: the user can override the
// value before saving; an empty submission resolves to the computed
// date on the server.
export const CALENDAR_TOKENS = [
  ':calendar_now',
  ':calendar_today',
  ':calendar_yesterday',
  ':calendar_tomorrow',
  ':calendar_week_start',
  ':calendar_week_end',
  ':calendar_next_week_start',
  ':calendar_next_week_end',
  ':calendar_prev_week_start',
  ':calendar_prev_week_end',
  ':calendar_month_start',
  ':calendar_month_end',
  ':calendar_next_month_start',
  ':calendar_next_month_end',
  ':calendar_prev_month_start',
  ':calendar_prev_month_end',
  ':calendar_quarter_start',
  ':calendar_quarter_end',
  ':calendar_next_quarter_start',
  ':calendar_next_quarter_end',
  ':calendar_prev_quarter_start',
  ':calendar_prev_quarter_end',
  ':calendar_year_start',
  ':calendar_year_end',
  ':calendar_next_year_start',
  ':calendar_next_year_end',
  ':calendar_prev_year_start',
  ':calendar_prev_year_end',
] as const

export type CalendarToken = (typeof CALENDAR_TOKENS)[number]

export const CALENDAR_TOKEN_LABELS: Record<CalendarToken, string> = {
  ':calendar_now': 'Now (current date + time)',
  ':calendar_today': 'Today',
  ':calendar_yesterday': 'Yesterday',
  ':calendar_tomorrow': 'Tomorrow',
  ':calendar_week_start': 'Week start (Monday of this week)',
  ':calendar_week_end': 'Week end (Sunday of this week)',
  ':calendar_next_week_start': 'Next week start',
  ':calendar_next_week_end': 'Next week end',
  ':calendar_prev_week_start': 'Previous week start',
  ':calendar_prev_week_end': 'Previous week end',
  ':calendar_month_start': 'Month start (first day of this month)',
  ':calendar_month_end': 'Month end (last day of this month)',
  ':calendar_next_month_start': 'Next month start',
  ':calendar_next_month_end': 'Next month end',
  ':calendar_prev_month_start': 'Previous month start',
  ':calendar_prev_month_end': 'Previous month end',
  ':calendar_quarter_start': 'Quarter start (first day of this quarter)',
  ':calendar_quarter_end': 'Quarter end (last day of this quarter)',
  ':calendar_next_quarter_start': 'Next quarter start',
  ':calendar_next_quarter_end': 'Next quarter end',
  ':calendar_prev_quarter_start': 'Previous quarter start',
  ':calendar_prev_quarter_end': 'Previous quarter end',
  ':calendar_year_start': 'Year start (Jan 1 of this year)',
  ':calendar_year_end': 'Year end (Dec 31 of this year)',
  ':calendar_next_year_start': 'Next year start (Jan 1)',
  ':calendar_next_year_end': 'Next year end (Dec 31)',
  ':calendar_prev_year_start': 'Previous year start (Jan 1)',
  ':calendar_prev_year_end': 'Previous year end (Dec 31)',
}

// Optional ``+N`` / ``-N`` day-offset suffix any calendar token accepts
// (``:calendar_today+7`` → a week from today). Mirrors the backend parser.
const CALENDAR_OFFSET_RE = /^(.*?)([+-]\d{1,4})$/

// Split a raw default into ``[baseToken, dayOffset]`` when it is a calendar
// token (with or without offset); null otherwise.
function splitCalendarToken(raw: string): [CalendarToken, number] | null {
  let tok = (raw || '').trim().toLowerCase()
  let offset = 0
  const m = CALENDAR_OFFSET_RE.exec(tok)
  if (m) {
    tok = m[1]
    offset = parseInt(m[2], 10)
  }
  if ((CALENDAR_TOKENS as readonly string[]).includes(tok)) {
    return [tok as CalendarToken, offset]
  }
  return null
}

// True when a RAW value (not a column's default) is a calendar token, with or
// without a day offset. Used where a default is authored for something that
// isn't a column — an enforced filter's bound — to tell whether the value
// will resolve to a date.
export function isCalendarTokenValue(raw: string | undefined | null): boolean {
  return !!splitCalendarToken((raw || '').toString())
}

export function calendarTokenOf(c: ColumnDef): string | null {
  const raw = (c?.default_value || '').toString().trim().toLowerCase()
  return splitCalendarToken(raw) ? raw : null
}

// Resolve a ``:calendar_*`` token to a concrete Date based on the
// current clock. Mirrors ``backend/app/services/livepreview/
// calendar_defaults.resolve_value`` so the designer's live preview
// shows the same value the runtime will stamp at INSERT time. Uses
// UTC components to stay aligned with the backend (which keys off
// ``datetime.now(timezone.utc)``).
export function resolveCalendarToken(token: string): Date | null {
  const parts = splitCalendarToken(token)
  if (!parts) return null
  const [tok, dayOffset] = parts

  const now = new Date()
  const today = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
  ))

  const addDays = (d: Date, n: number) =>
    new Date(d.getTime() + n * 86400000)
  const addMonths = (d: Date, n: number) => {
    const total = d.getUTCMonth() + n
    const year = d.getUTCFullYear() + Math.floor(total / 12)
    const month = ((total % 12) + 12) % 12
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
    return new Date(Date.UTC(year, month, Math.min(d.getUTCDate(), lastDay)))
  }
  const monthStart = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
  const monthEnd = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))
  // ISO week — Monday is day 0. JS getUTCDay: Sun=0..Sat=6.
  const weekStart = (d: Date) => {
    const iso = (d.getUTCDay() + 6) % 7
    return addDays(d, -iso)
  }
  const weekEnd = (d: Date) => addDays(weekStart(d), 6)
  const quarterStart = (d: Date) => {
    const q = Math.floor(d.getUTCMonth() / 3)
    return new Date(Date.UTC(d.getUTCFullYear(), q * 3, 1))
  }
  const quarterEnd = (d: Date) => monthEnd(addMonths(quarterStart(d), 2))

  const resolved = ((): Date | null => {
  switch (tok) {
    case ':calendar_now': return now
    case ':calendar_today': return today
    case ':calendar_yesterday': return addDays(today, -1)
    case ':calendar_tomorrow': return addDays(today, 1)
    case ':calendar_week_start': return weekStart(today)
    case ':calendar_week_end': return weekEnd(today)
    case ':calendar_next_week_start': return addDays(weekStart(today), 7)
    case ':calendar_next_week_end': return addDays(weekEnd(today), 7)
    case ':calendar_prev_week_start': return addDays(weekStart(today), -7)
    case ':calendar_prev_week_end': return addDays(weekEnd(today), -7)
    case ':calendar_month_start': return monthStart(today)
    case ':calendar_month_end': return monthEnd(today)
    case ':calendar_next_month_start': return monthStart(addMonths(today, 1))
    case ':calendar_next_month_end': return monthEnd(addMonths(today, 1))
    case ':calendar_prev_month_start': return monthStart(addMonths(today, -1))
    case ':calendar_prev_month_end': return monthEnd(addMonths(today, -1))
    case ':calendar_quarter_start': return quarterStart(today)
    case ':calendar_quarter_end': return quarterEnd(today)
    case ':calendar_next_quarter_start':
      return quarterStart(addMonths(quarterStart(today), 3))
    case ':calendar_next_quarter_end':
      return quarterEnd(addMonths(quarterStart(today), 3))
    case ':calendar_prev_quarter_start':
      return quarterStart(addMonths(quarterStart(today), -3))
    case ':calendar_prev_quarter_end':
      return quarterEnd(addMonths(quarterStart(today), -3))
    case ':calendar_year_start':
      return new Date(Date.UTC(today.getUTCFullYear(), 0, 1))
    case ':calendar_year_end':
      return new Date(Date.UTC(today.getUTCFullYear(), 11, 31))
    case ':calendar_next_year_start':
      return new Date(Date.UTC(today.getUTCFullYear() + 1, 0, 1))
    case ':calendar_next_year_end':
      return new Date(Date.UTC(today.getUTCFullYear() + 1, 11, 31))
    case ':calendar_prev_year_start':
      return new Date(Date.UTC(today.getUTCFullYear() - 1, 0, 1))
    case ':calendar_prev_year_end':
      return new Date(Date.UTC(today.getUTCFullYear() - 1, 11, 31))
  }
  return null
  })()
  if (resolved && dayOffset) return addDays(resolved, dayOffset)
  return resolved
}

// Convenience: resolve a calendar-token default to the string value
// expected by an ``<input type=...>`` control. Returns ``null`` when
// ``defaultValue`` is not a calendar token, so callers can fall
// through to their existing default-value handling.
export function calendarDefaultForInput(
  defaultValue: string | null | undefined,
  inputType: string,
): string | null {
  if (!defaultValue) return null
  const d = resolveCalendarToken(defaultValue)
  if (!d) return null
  if (inputType === 'date') return d.toISOString().slice(0, 10)
  if (inputType === 'datetime-local') return d.toISOString().slice(0, 16)
  // text / number / other — emit an ISO date string so the user can
  // still see the resolved value rather than the raw token.
  return d.toISOString().slice(0, 10)
}

// Legacy function-call aliases used by generated metadata. The runtime
// treats these as audit tokens too, disambiguating add vs chg by the
// column name prefix (``ADD_*`` → add-token, ``CHG_*`` → chg-token).
const AUDIT_ALIAS_SUFFIX: Record<string, '_date' | '_user' | '_terminal'> = {
  'now()': '_date',
  'logged_in_user()': '_user',
  'logged_in_terminal()': '_terminal',
}

export function auditTokenOf(c: ColumnDef): AuditToken | null {
  const raw = (c?.default_value || '').toString().trim().toLowerCase()
  if ((AUDIT_TOKENS as readonly string[]).includes(raw)) {
    return raw as AuditToken
  }
  const suffix = AUDIT_ALIAS_SUFFIX[raw]
  if (!suffix) return null
  const dbName = (c?.db_name || c?.name || '').toString().trim().toLowerCase()
  if (dbName.startsWith('add_')) return `:audit_add${suffix}` as AuditToken
  if (dbName.startsWith('chg_')) return `:audit_chg${suffix}` as AuditToken
  return null
}

// ── Project-variable tokens ──────────────────────────────────────────────
// Any leading-colon value that isn't a reserved audit name is treated
// as a project-level variable lookup (resolved at INSERT time, only
// when the form posted no value).

export function projectVarOf(c: ColumnDef): string | null {
  const raw = (c?.default_value || '').toString().trim()
  if (!raw.startsWith(TOKEN_PREFIX)) return null
  const name = raw.slice(TOKEN_PREFIX.length).trim().toLowerCase()
  if (!name) return null
  if ((AUDIT_TOKENS as readonly string[]).includes(`${TOKEN_PREFIX}${name}`)) {
    return null  // owned by audit-defaults
  }
  if (name.startsWith('calendar_')) {
    return null  // owned by calendar-defaults
  }
  return name
}

export function projectVarToken(name: string): string {
  return `${TOKEN_PREFIX}${name.trim().toLowerCase()}`
}

// `auto_generate_line_no()` — a parent-scoped serial: the row's line number
// WITHIN its parent (each parent's detail rows number 1, 2, 3… independently),
// used on a detail form's `line_no` and a grandchild's own discriminator. The
// engine resolves it on INSERT, scoped by the row's other key columns.
export function isAutoGenerateLineNo(c: ColumnDef): boolean {
  const raw = (c?.default_value || '').toString().trim().toLowerCase()
  return raw === 'auto_generate_line_no()' || raw === 'auto_generate_line_no ()'
}

// `auto_generate_device()` — a per-device series (`INV-D03-0001`) that lets a
// disconnected device mint a real document number with no central coordination.
export function isAutoGenerateDevice(c: ColumnDef): boolean {
  const raw = (c?.default_value || '').toString().trim().toLowerCase()
  return raw === 'auto_generate_device()' || raw === 'auto_generate_device ()'
}

// `auto_generate_prefixed()` — a serial counted independently per prefix, where
// the prefix is composed from the row's OWN data (a column or a substring of it,
// fixed text, a date part, a master-table lookup). An artist master keyed on
// country numbers IND0000001, IND0000002, USA0000001.
export function isAutoGeneratePrefixed(c: ColumnDef): boolean {
  const raw = (c?.default_value || '').toString().trim().toLowerCase()
  return raw === 'auto_generate_prefixed()' || raw === 'auto_generate_prefixed ()'
}

// `auto_generate()` — column gets a server-stamped serial number at
// INSERT time. Recognised case-insensitively; whitespace around the
// parens is tolerated. The field stays editable so the user can still
// type an explicit value; an empty submission triggers auto-fill.
// Returns true for EVERY auto-generate token (table-wide serial, parent-scoped
// line number, per-device series and per-prefix series) — all are server-
// resolved, so the form treats them identically: skip literal seeding, show
// the "Auto" hint.
export function isAutoGenerate(c: ColumnDef): boolean {
  const raw = (c?.default_value || '').toString().trim().toLowerCase()
  return raw === 'auto_generate()' || raw === 'auto_generate ()'
    || isAutoGenerateLineNo(c)
    || isAutoGenerateDevice(c)
    || isAutoGeneratePrefixed(c)
}

// A function-call-style default — `today()`, `now()`, `add_days(...)`,
// `logged_in_user()`, `logged_in_terminal()`, … These are resolved
// SERVER-SIDE at INSERT by the runtime engine (field_defaults.py), so the
// Add form must NOT seed the literal token text: posting it back verbatim
// makes the engine treat it as a caller-supplied value (caller wins) and
// the raw token gets written into the column. Mirrors the engine's
// `_FUNC_RE`. Colon tokens (`:calendar_*`, project vars) don't match this
// shape and are handled by their own resolvers.
const FUNCTION_DEFAULT_RE = /^\s*[A-Za-z_][A-Za-z0-9_]*\s*\(.*\)\s*$/

export function isFunctionCallDefault(c: ColumnDef): boolean {
  const raw = (c?.default_value || '').toString().trim()
  return raw !== '' && FUNCTION_DEFAULT_RE.test(raw)
}

// True if the column's default_value is any kind of resolve-at-save
// token (audit, project variable, or auto-generate). Used by the form
// to mark the field with an "Auto" hint.
export function isAutoStampedColumn(c: ColumnDef): boolean {
  return auditTokenOf(c) !== null
    || projectVarOf(c) !== null
    || isAutoGenerate(c)
}

export function mainSqlModel(model: TransactionModel): SqlModel | null {
  const first = model.sql_models?.[0]?.sql_model
  return first || null
}

// All sql_models in declaration order. Each entry carries a synthetic
// form_no (falling back to the index+1) so the runtime can address any
// form even when the metadata omits the field.
export function allSqlModels(model: TransactionModel): Array<{
  formNo: string
  sm: SqlModel
}> {
  const out: Array<{ formNo: string; sm: SqlModel }> = []
  const wrappers = model.sql_models || []
  wrappers.forEach((w, i) => {
    const sm = w?.sql_model
    if (!sm) return
    const raw = (sm as unknown as { form_no?: string | number }).form_no
    const formNo = raw != null && String(raw).trim() !== ''
      ? String(raw).trim()
      : String(i + 1)
    out.push({ formNo, sm })
  })
  return out
}

// ── Form hierarchy (parent_form_no) ────────────────────────────────────
// Transactions are a forest of forms: the header (the first declared form)
// is the root, and every other form names its immediate parent via
// `parent_form_no`. An absent / empty / self / header value means "direct
// child of the header", preserving the historical positional 2-level
// convention. Every walk below carries a visited-set so a hand-edited
// cyclic metadata can never hang the designer or runtime.

// The header form's form_no (the first declared sql_model). "1" for an
// empty model.
export function headerFormNo(model: TransactionModel): string {
  return allSqlModels(model)[0]?.formNo ?? '1'
}

// The immediate parent form_no for one sql_model entry. Empty / self ⇒ the
// header.
export function parentFormNoOf(
  entry: { formNo: string; sm: SqlModel }, headerNo: string,
): string {
  const raw = String(entry.sm?.parent_form_no ?? '').trim()
  if (!raw || raw === entry.formNo) return headerNo
  return raw
}

// All forms whose immediate parent is `parentFormNo`, in declaration order.
// The header is never returned as a child even if it self-references.
export function childFormsOf(
  model: TransactionModel, parentFormNo: string,
): Array<{ formNo: string; sm: SqlModel }> {
  const headerNo = headerFormNo(model)
  return allSqlModels(model).filter(e =>
    e.formNo !== headerNo && parentFormNoOf(e, headerNo) === parentFormNo,
  )
}

// Transitive descendant form_nos of `formNo` (excludes `formNo` itself).
// Used by the designer's parent picker to forbid choosing a descendant —
// the only way to introduce a cycle in a single-parent forest.
export function descendantFormNos(
  model: TransactionModel, formNo: string,
): Set<string> {
  const out = new Set<string>()
  const queue: string[] = [formNo]
  const visited = new Set<string>([formNo])
  while (queue.length) {
    const cur = queue.shift() as string
    for (const child of childFormsOf(model, cur)) {
      if (visited.has(child.formNo)) continue
      visited.add(child.formNo)
      out.add(child.formNo)
      queue.push(child.formNo)
    }
  }
  return out
}

// Distance from the header (header = 0, its direct child = 1, …). A cycle
// short-circuits to MAX_SAFE_INTEGER so depth-capping callers treat it as
// "too deep" rather than looping.
export function formDepth(model: TransactionModel, formNo: string): number {
  const headerNo = headerFormNo(model)
  const byFormNo = new Map(allSqlModels(model).map(e => [e.formNo, e]))
  let depth = 0
  let cur = formNo
  const visited = new Set<string>()
  while (cur !== headerNo) {
    if (visited.has(cur)) return Number.MAX_SAFE_INTEGER
    visited.add(cur)
    const entry = byFormNo.get(cur)
    if (!entry) return depth // dangling parent_form_no ⇒ stop the walk
    const parent = parentFormNoOf(entry, headerNo)
    depth += 1
    if (parent === cur) return depth
    cur = parent
  }
  return depth
}

// Primary-key db_names on a sql_model (lowercase).
export function pkDbNames(sm: SqlModel): string[] {
  return (sm.columns || [])
    .map(w => w.column)
    .filter(c => !!c && (c.key === true || String(c.key).toLowerCase() === 'true'))
    .map(c => columnDbName(c))
}

// Child columns that reference the parent's PK (by name match).
export function parentFkColumns(child: SqlModel, parent: SqlModel): string[] {
  const parentPks = new Set(pkDbNames(parent))
  return pkDbNames(child).filter(n => parentPks.has(n))
}

// Build the parent_pk dict the backend expects: { db_name: value }
// for every parent PK column present on the child as a FK. Reads the
// value from the parent's form state via the column's form_key (so the
// lookup still works when name and db_name differ).
export function buildParentPk(
  parent: SqlModel, parentValues: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const w of parent.columns || []) {
    const c = w?.column
    if (!c) continue
    const isPk = c.key === true || String(c.key).toLowerCase() === 'true'
    if (!isPk) continue
    const fkey = columnFormKey(c)
    const dbn = columnDbName(c)
    if (fkey in parentValues) {
      out[dbn] = parentValues[fkey]
    } else if (dbn in parentValues) {
      out[dbn] = parentValues[dbn]
    }
  }
  return out
}

// Physical DB column name. Use when talking to the backend about a
// specific column (e.g. parent_filter, list extra_filters, lookup
// targets). Joined-master columns may share a db_name across the form;
// use `columnFormKey` for anything keyed against form state.
export function columnDbName(c: ColumnDef): string {
  const raw = (c.db_name || c.name || '').trim()
  return raw.replace(/\s+/g, '_').toLowerCase()
}

// Form-state key for a column. Within a single form, the user-facing
// `name` is unique (transaction designer enforces it), so it's the
// stable per-field identity for form state, errors, item_change
// updates, etc. Falls back to db_name only when name is missing.
//
// Why not db_name? Two joined-master columns can share a db_name —
// e.g. `task.descr` and `task_type.descr` both have db_name "descr" —
// which collapses sibling fields onto the same slot.
export function columnFormKey(c: ColumnDef): string {
  const raw = (c.name || c.db_name || '').trim()
  return raw.replace(/\s+/g, '_').toLowerCase()
}

// The `<table>_<column>` doc-type a document/image upload must carry so the
// deployed-site upload endpoint can scope it to a real document column of the
// transaction (page-bound anonymous uploads are rejected otherwise). Mirrors
// the backend's ddl_helpers.table_db_name / column_db_name: prefer db_name,
// spaces → underscores, lower-cased. Empty when the column lacks a table.
export function columnDocType(c: ColumnDef): string {
  const tbl = (c.table_name || '').trim().replace(/\s+/g, '_').toLowerCase()
  const col = (c.db_name || c.name || '').trim().replace(/\s+/g, '_').toLowerCase()
  return tbl && col ? `${tbl}_${col}` : ''
}

// Metadata stores boolean-ish flags inconsistently: sometimes `true`,
// sometimes the string `"true"`, sometimes `"1"` (and rarely `"yes"`).
// Treat any of these as truthy so e.g. `protect: "1"` actually disables
// the input.
function _truthy(v: unknown): boolean {
  if (v === true) return true
  if (v == null || v === false) return false
  const s = String(v).trim().toLowerCase()
  return s === 'true' || s === '1' || s === 'yes' || s === 'y' || s === 't'
}

export function isMandatory(c: ColumnDef): boolean {
  return _truthy(c.mandatory)
}

/** The caption a LIST / grid / filter shows for a column: ``list_heading``
 *  when set, else the form ``heading``, else the name. */
export function listHeadingOf(c: Pick<ColumnDef, 'name'> & Partial<Pick<ColumnDef, 'heading' | 'list_heading'>>): string {
  const lh = String(c.list_heading ?? '').trim()
  return lh || c.heading || c.name
}

export function isProtect(c: ColumnDef): boolean {
  return _truthy(c.protect) || isComputedColumn(c)
}

/** The form shows no caption for this field (``hide_label``). */
export function isLabelHidden(c: ColumnDef): boolean {
  return _truthy(c.hide_label)
}

/**
 * Split a group's columns into the fields that own a cell and the fields
 * drawn beside them (``inline_with``), keyed by the owner's form key. A chain
 * (EMP_LNAME beside EMP_FNAME beside EMP_CODE__ORD) lands in the first
 * field's cell, in order. A field whose partner is not among ``cols`` (hidden,
 * or in another group) keeps a cell of its own.
 */
export function inlineLayout(cols: ColumnDef[]): {
  owners: ColumnDef[]; besides: Map<string, ColumnDef[]>
} {
  const byName = new Map<string, ColumnDef>()
  cols.forEach(c => byName.set(String(c.name || '').toUpperCase(), c))
  const rootOf = (c: ColumnDef): ColumnDef => {
    let cur = c
    const seen = new Set<ColumnDef>()
    while (cur.inline_with && !seen.has(cur)) {
      seen.add(cur)
      const next = byName.get(String(cur.inline_with).toUpperCase())
      if (!next || next === cur) break
      cur = next
    }
    return cur
  }
  const owners: ColumnDef[] = []
  const besides = new Map<string, ColumnDef[]>()
  cols.forEach(c => {
    const root = rootOf(c)
    if (root === c) { owners.push(c); return }
    const key = columnFormKey(root)
    if (!besides.has(key)) besides.set(key, [])
    besides.get(key)!.push(c)
  })
  return { owners, besides }
}

/**
 * Continuation lines: a ``hide_label`` field that is the next LINE of the
 * field above it (ADDR2 / ADDR3 under ADDR1, BANK_ADDR2 under BANK_ADDR1) is
 * drawn stacked under that field in the same cell instead of taking an
 * auto-fit cell of its own with an empty caption.
 *
 * ``cols`` is the group's columns in render order (y, then index) and
 * ``owners`` the cell owners from {@link inlineLayout}. The model's ``x`` is a
 * list sequence, not the screen x, so the rule reads only y and width: a
 * caption-less owner stacks under the IMMEDIATELY preceding column when that
 * column owns a cell itself (is not drawn beside another), sits on a line
 * above it (smaller y, at most ~two Vision lines up) and is about as wide
 * (within 10% / 24px). Chains (ADDR3 under ADDR2 under ADDR1) all land under
 * the first field. Returned keyed by the anchor's form key; ``owners`` keeps
 * only the fields that still own a cell.
 */
export function continuationLayout(cols: ColumnDef[], owners: ColumnDef[]): {
  owners: ColumnDef[]; below: Map<string, ColumnDef[]>
} {
  const ownerSet = new Set(owners)
  const anchorOf = new Map<ColumnDef, ColumnDef>()
  const below = new Map<string, ColumnDef[]>()
  for (let i = 1; i < cols.length; i++) {
    const c = cols[i]
    if (!ownerSet.has(c) || !isLabelHidden(c) || c.inline_with) continue
    const prev = cols[i - 1]
    if (!ownerSet.has(prev)) continue
    const dy = (Number(c.y) || 0) - (Number(prev.y) || 0)
    if (!(dy > 0 && dy <= 48)) continue
    const wc = Number(c.width) || 0
    const wp = Number(prev.width) || 0
    if (!(wc > 0 && wp > 0 && Math.abs(wc - wp) <= Math.max(24, 0.1 * Math.max(wc, wp)))) continue
    const anchor = anchorOf.get(prev) ?? prev
    anchorOf.set(c, anchor)
    const key = columnFormKey(anchor)
    if (!below.has(key)) below.set(key, [])
    below.get(key)!.push(c)
  }
  return { owners: owners.filter(c => !anchorOf.has(c)), below }
}

/** The Vision display format a FORM field honours (``format`` like
 *  "[Percentage]" / "[Email]" / "[Phone]" / "[WebUrl]" / "[Map]"),
 *  normalised case-insensitively; null for any other. */
export function visionFieldFormat(
  c: ColumnDef,
): 'percentage' | 'email' | 'phone' | 'weburl' | 'map' | null {
  const f = String(c.format ?? '').trim().replace(/^\[|\]$/g, '').trim().toLowerCase()
  if (f === 'percentage' || f === 'percent') return 'percentage'
  if (f === 'email' || f === 'mail') return 'email'
  if (f === 'phone' || f === 'telephone' || f === 'tel') return 'phone'
  if (f === 'weburl' || f === 'url' || f === 'web url') return 'weburl'
  if (f === 'map') return 'map'
  return null
}

/** The transaction's key column is generated by the engine at INSERT
 *  (``transaction.key_generation`` in Vision mode), so the add form must not
 *  ask for it. */
export function isGeneratedKeyColumn(
  c: ColumnDef,
  keyGeneration?: { mode?: string; key_col?: string } | null,
): boolean {
  if (!keyGeneration) return false
  if (String(keyGeneration.mode ?? '').trim().toLowerCase() !== 'vision') return false
  const keyCol = String(keyGeneration.key_col ?? '').trim().toLowerCase()
  if (!keyCol) return false
  return String(c.name ?? '').trim().toLowerCase() === keyCol
    || String(c.db_name ?? '').trim().toLowerCase() === keyCol
}

/** A column whose value the database works out on read (``sql_expression``):
 *  always read-only, never saved. */
export function isComputedColumn(c: ColumnDef): boolean {
  return String(c.sql_expression ?? '').trim() !== ''
}

/** ``v`` in the column's ``text_case`` — codes a screen enters in capitals
 *  are typed that way. The engine applies the same rule on every write. */
export function applyTextCase(c: ColumnDef, v: string): string {
  const mode = String(c.text_case ?? '').trim().toLowerCase()
  if (mode === 'upper') return v.toUpperCase()
  if (mode === 'lower') return v.toLowerCase()
  return v
}

// ── Per-record edit lock ──────────────────────────────────────────────────
// The engine stamps each list row with ``_editable`` (and ``_lock_reason``
// when locked) so a row that is not editable — because it matches a designer
// edit-lock rule or has an in-flight approval — opens view-only with a View
// indicator. Absent ``_editable`` (legacy rows) ⇒ editable.
export function rowEditable(row: Record<string, unknown> | null | undefined): boolean {
  if (!row) return true
  return row._editable !== false
}

export function rowLockReason(row: Record<string, unknown> | null | undefined): string {
  const r = row && row._lock_reason
  return typeof r === 'string' ? r : ''
}

// ── Per-row action visibility ─────────────────────────────────────────────
// An action may carry a ``visible_when`` expression. The ENGINE evaluates it
// per row (it owns that expression language) and stamps the names it resolved
// falsy onto the row as ``_hidden_actions``; every list drops those entries
// from that row's menu. Absent (legacy rows, or no action declares one) ⇒
// every action shows, which is the behaviour before this existed.
//
// Hiding is presentation only — the action's pre-action validations are what
// actually refuse it. Never treat a visible button as permission to run.
export function rowHiddenActions(
  row: Record<string, unknown> | null | undefined,
): string[] {
  const h = row && row._hidden_actions
  return Array.isArray(h) ? h.filter((n): n is string => typeof n === 'string') : []
}

/**
 * Actions to draw in this row's menu — those the engine did not hide.
 * Takes the hidden NAMES, not the row: a list renderer reads them straight off
 * the row (`rowHiddenActions`), while the capture grid lifts them onto its
 * EmbedRecord (annotations are stripped from its header so they never ride
 * back to the server on a save).
 */
export function visibleRowActions<T extends { name: string }>(
  actions: T[] | undefined,
  hidden: string[] | undefined,
): T[] {
  if (!hidden || hidden.length === 0) return actions || []
  return (actions || []).filter(a => !hidden.includes(a.name))
}

/** Open one record the way the designer said it may be opened.
 *
 * A `system_op: 'edit'` (or `'view'`) action carries PRE-ACTION VALIDATIONS —
 * "only an Open deal may be edited" — and those conditions are the whole point
 * of declaring the action. They run on the server, inside `run_action`, so
 * opening a record by calling the editor DIRECTLY skips them: the record opens,
 * the rule never fires, and the constraint the designer wrote is not a
 * constraint any more.
 *
 * So every way of opening a record — the list's pencil, a row click, the
 * compact card, the kebab on a process card or inside a trail — routes through
 * here. An EDITABLE record goes through the edit action; a locked one goes
 * through the VIEW action instead, because the edit action's conditions must
 * not fire for someone who is only looking. With no such action declared, the
 * editor is opened directly (and still honours the engine's own row verdict).
 */
export function openRecord(o: {
  txnId: string
  /** The engine's per-row verdict. Absent / true ⇒ editable. */
  editable?: boolean
  /** The object's merged system actions, from `partitionActionsByScope`. */
  systemOverrides?: { edit?: { name: string }; view?: { name: string } }
  /** Runs one action button against this record, gates and all. */
  onRunRowAction?: (name: string, txnId: string) => void | Promise<void>
  /** Opens the editor directly — the fallback when no action gates the open. */
  onOpen: (txnId: string, opts: { view: boolean }) => void
}): void {
  const view = o.editable === false
  const gate = o.onRunRowAction
    ? (view ? o.systemOverrides?.view : o.systemOverrides?.edit)
    : undefined
  if (gate) {
    void o.onRunRowAction!(gate.name, o.txnId)
    return
  }
  o.onOpen(o.txnId, { view })
}

// The form's current mode for the add/edit/view indicator. ``viewOnly`` wins
// (record locked or user lacks edit rights); otherwise an existing record
// (``txnId``) is an edit and a fresh one is an add.
export function formMode(opts: { viewOnly?: boolean; txnId?: string | null }): 'add' | 'edit' | 'view' {
  if (opts.viewOnly) return 'view'
  return opts.txnId ? 'edit' : 'add'
}

export function isKey(c: ColumnDef): boolean {
  return _truthy(c.key)
}

export function isHidden(c: ColumnDef): boolean {
  return _truthy(c.hidden)
}

/** Hidden from the read-only LIST grid: ``list_hidden`` when set, else
 *  ``hidden`` (see ColumnDef.list_hidden). */
export function isListHidden(c: ColumnDef): boolean {
  const lh = c.list_hidden
  if (lh !== undefined && lh !== null && String(lh).trim() !== '') return _truthy(lh)
  return isHidden(c)
}

export function inputType(c: ColumnDef): string {
  const t = (c.col_type || '').toUpperCase()
  switch (t) {
    case 'NUMBER':
    case 'NUMERIC':
    case 'DECIMAL':
    case 'INTEGER':
    case 'INT':
    case 'BIGINT':
    case 'SMALLINT':
    case 'DOUBLE':
    case 'FLOAT':
    case 'REAL':
      return 'number'
    case 'DATE': return 'date'
    case 'DATETIME':
    case 'TIMESTAMP':
    case 'TIMESTAMPTZ':
      return 'datetime-local'
    case 'TIME': return 'time'
    case 'BOOLEAN':
    case 'BOOL':
      return 'checkbox'
    default: return 'text'
  }
}

// A TIMESTAMPTZ column stores an instant: its date box shows local time and
// must write back with the browser's offset (see `dateInput.withLocalZone`).
export function isZonedColumn(c: ColumnDef): boolean {
  return (c.col_type || '').toString().trim().toUpperCase() === 'TIMESTAMPTZ'
}

// JSON/JSONB columns edit as an auto-grow monospace textarea (validated on
// blur) unless an explicit edit_mask widget overrides them.
export function isJsonColumn(c: ColumnDef): boolean {
  const t = (c.col_type || '').toUpperCase()
  return t === 'JSON' || t === 'JSONB'
}

// RICHTEXT columns hold sanitised HTML (headings, lists, links, tables,
// images) and edit in the rich-text editor; grids show their plain text.
export function isRichTextColumn(c: ColumnDef): boolean {
  return (c.col_type || '').toString().trim().toUpperCase() === 'RICHTEXT'
}

// A RICHTEXT value is markup; wherever a cell shows it as TEXT (grid cells,
// tooltips, cards, feeds) it reads as its visible words. Null for any other
// column so callers fall through to their own formatting.
export function richCellText(value: unknown, c?: ColumnDef): string | null {
  if (!c || !isRichTextColumn(c)) return null
  return richPlainText(value)
}

export function hasDropdown(c: ColumnDef): boolean {
  return c.edit_mask?.mask === 'ddlb'
    && Array.isArray(c.edit_mask?.values)
    && c.edit_mask.values.length > 0
}

// ── Presentation widgets over a plain text/varchar column ───────────────
// These don't carry edit_mask.values; the mask alone selects the widget.

export function hasTextarea(c: ColumnDef): boolean {
  return c.edit_mask?.mask === 'textarea'
}

// Starting height (CSS px) of a textarea whose mask carries a ``rows`` hint
// (a Vision multi-line field's height in lines); the default otherwise.
export function textareaMinHeight(c: ColumnDef): number {
  const rows = Number(c.edit_mask?.rows)
  if (!Number.isFinite(rows) || rows <= 0) return TEXTAREA_MIN_HEIGHT
  // ~19px per line at the form's font size (line-height 1.45) + padding.
  return Math.min(TEXTAREA_MAX_HEIGHT, Math.max(36, Math.round(rows * 19 + 16)))
}

export function hasUrlWidget(c: ColumnDef): boolean {
  return c.edit_mask?.mask === 'url'
}

export function hasImageUpload(c: ColumnDef): boolean {
  return c.edit_mask?.mask === 'image'
}

export function hasDocumentUpload(c: ColumnDef): boolean {
  return c.edit_mask?.mask === 'document'
}

// True for any file-upload widget (image or document). Both store the
// document-store URL of the uploaded file as the column's string value.
export function hasFileUpload(c: ColumnDef): boolean {
  return hasImageUpload(c) || hasDocumentUpload(c)
}

// `accept` attribute for a file-upload widget's <input type="file">.
// Image widgets accept images only; document widgets accept the common
// office/pdf/text formats the document store allows.
export function fileUploadAccept(c: ColumnDef): string {
  if (hasImageUpload(c)) return 'image/*'
  return '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.odt,.ods,.json,.xml,image/*,application/pdf'
}

// Auto-grow bounds for a textarea widget (in CSS px). The box starts at
// ``min`` and grows with content up to ``max`` — past which it scrolls so
// a long note can't blow out the form layout.
export const TEXTAREA_MIN_HEIGHT = 56
export const TEXTAREA_MAX_HEIGHT = 220

// CHAR(N) columns come back space-padded from the DB (e.g. "CLASSICAL "),
// while a ddlb/radio option's `data` is unpadded ("CLASSICAL"). An exact
// compare then fails, so a <select>/radio can't restore its selection when
// editing an existing record. These helpers match a stored value to its
// option ignoring surrounding whitespace (and case, mirroring the grid's
// display lookup).
function normaliseOptionKey(v: unknown): string {
  return String(v ?? '').trim().toLowerCase()
}

// Canonical option `data` for a stored value, so an HTML <select> matches
// one of its <option value=...> entries despite CHAR padding. Falls back to
// the raw string when no option matches (preserves legacy/unknown values).
export function ddlbSelectValue(
  value: unknown,
  values?: Array<{ data?: unknown }>,
): string {
  if (value == null) return ''
  const key = normaliseOptionKey(value)
  const match = (values || []).find(o => normaliseOptionKey(o?.data) === key)
  return match ? String(match.data) : String(value)
}

// Whitespace/case-insensitive equality for radio "checked" state.
export function optionValueEquals(a: unknown, b: unknown): boolean {
  if (a == null) return false
  return normaliseOptionKey(a) === normaliseOptionKey(b)
}

// Resolve a cell's user-facing label for ddlb/radio/checkbox/bool_check
// columns: map the stored "data" code to its edit_mask "display" label
// (whitespace/case-insensitive, so CHAR-padded values still match). Returns
// null when the column isn't an option mask, has no options, or the value
// doesn't match any option — callers then fall back to their own formatting.
export function ddlbDisplayLabel(value: unknown, col: ColumnDef): string | null {
  const mask = col.edit_mask?.mask
  if (mask !== 'ddlb' && mask !== 'radio' && mask !== 'checkbox' && mask !== 'bool_check') {
    return null
  }
  const values = col.edit_mask?.values
  if (!Array.isArray(values) || values.length === 0) return null
  // bool_check may surface as a native boolean / 0|1 (BOOLEAN col) instead
  // of the literal data code — map it to the right slot before matching.
  let lookup: unknown = value
  if (mask === 'bool_check' && typeof value === 'boolean') {
    lookup = values[value ? 0 : 1]?.data ?? value
  } else if (mask === 'bool_check' && typeof value === 'number') {
    lookup = values[value !== 0 ? 0 : 1]?.data ?? value
  }
  const match = values.find(v => optionValueEquals(v.data, lookup))
  return match && match.display ? String(match.display) : null
}

export function hasRadioValues(c: ColumnDef): boolean {
  return c.edit_mask?.mask === 'radio'
    && Array.isArray(c.edit_mask?.values)
    && c.edit_mask.values.length > 0
}

export function hasCheckboxValues(c: ColumnDef): boolean {
  return c.edit_mask?.mask === 'checkbox'
    && Array.isArray(c.edit_mask?.values)
    && c.edit_mask.values.length > 0
}

// Single binary checkbox with a Checked-Value / Unchecked-Value pair.
export function hasBoolCheck(c: ColumnDef): boolean {
  return c.edit_mask?.mask === 'bool_check'
    && Array.isArray(c.edit_mask?.values)
    && c.edit_mask.values.length >= 2
}

// Checked/unchecked data values for a bool_check edit_mask. Slot 0 is
// the "checked" value, slot 1 is "unchecked". Falls back to Y/N — the
// runtime/designer default — when slots are missing or empty.
export function boolCheckValues(c: ColumnDef): { checked: string; unchecked: string } {
  const values = c.edit_mask?.values
  const checked = String(values?.[0]?.data ?? 'Y')
  const unchecked = String(values?.[1]?.data ?? 'N')
  return { checked, unchecked }
}

// True when ``value`` represents the "checked" state of a bool_check
// column. The backend may hand the value back as the literal edit_mask
// code ('Y' / 'N' / 'A' / 'I' / …) for CHAR/VARCHAR columns OR as a
// native boolean / 0|1 when the column's col_type is BOOLEAN — the
// JSON layer then surfaces it as ``true``/``false``. Treat both shapes
// as "checked" so the cell renders the same value the user saved.
export function isBoolCheckOn(value: unknown, checkedData: string): boolean {
  if (value == null || value === '') return false
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  const s = String(value).trim().toLowerCase()
  if (s === checkedData.trim().toLowerCase()) return true
  // Permissive fallback: anything Postgres-style "truthy" reads as
  // checked, so a Y/N edit_mask still picks up a value the backend
  // chose to return as ``true``/``1``.
  return s === 'true' || s === 't' || s === '1' || s === 'y' || s === 'yes'
}

// A bool_check with no value yet (null / empty) is neither checked nor
// unchecked; a toggle draws it neutral until the user picks.
export function isBoolCheckUnset(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '')
}

// A bool_check toggle may carry more values than its ON / OFF pair (Vision's
// "Yesno" edit style over Item's Active: Yes / No / On Alert). A stored value
// that is neither ON nor OFF but is one of those extra values returns its
// display text ("On Alert") — the toggle shows it in the neutral state rather
// than reading it as "No". Null otherwise.
export function boolCheckOtherDisplay(c: ColumnDef, value: unknown): string | null {
  if (isBoolCheckUnset(value) || typeof value === 'boolean' || typeof value === 'number') return null
  const values = c.edit_mask?.values || []
  if (values.length < 3) return null
  const key = String(value).trim().toLowerCase()
  const { checked, unchecked } = boolCheckValues(c)
  if (key === checked.trim().toLowerCase() || key === unchecked.trim().toLowerCase()) return null
  const hit = values.slice(2).find(v => String(v?.data ?? '').trim().toLowerCase() === key)
  return hit ? String(hit.display ?? hit.data) : null
}

export function hasLookup(c: ColumnDef): boolean {
  // Advanced (raw SQL) lookup needs no declarative spec.
  if ((c.lookup_sql || '').trim().length > 0) return true
  const spec = (c.lookup || '').trim()
  return spec.length > 0 && /^[A-Za-z_][A-Za-z0-9_]*\s*\([^,]+,[^)]+\)\s*$/.test(spec)
}

export function visibleColumns(sm: SqlModel): ColumnDef[] {
  return (sm.columns || [])
    .map(w => w.column)
    .filter(c => !!c && !isHidden(c))
}

// The values a NEW row of `sm` starts with: each visible column's literal
// `default_value`, and calendar tokens resolved to a concrete date. Server-
// resolved defaults (audit / project-variable / auto_generate / function-call
// tokens) are left blank — seeding their literal text would post it back as
// the value and bypass the resolution done at INSERT. One rule for the header
// add form, the capture embeds and every new grid line (Add row and paste).
export function literalDefaults(sm: SqlModel | null | undefined): Record<string, unknown> {
  const initial: Record<string, unknown> = {}
  if (!sm) return initial
  for (const col of visibleColumns(sm)) {
    const dv = (col.default_value || '').trim()
    if (!dv) continue
    if (auditTokenOf(col) || projectVarOf(col) || isAutoGenerate(col)) continue
    if (calendarTokenOf(col)) {
      const calVal = calendarDefaultForInput(dv, inputType(col))
      if (calVal) initial[columnFormKey(col)] = calVal
      continue
    }
    if (isFunctionCallDefault(col)) continue
    if (isEngineFilledKeyword(col, dv)) continue
    initial[columnFormKey(col)] = dv
  }
  return initial
}

// Vision keywords an imported model carries as a bare default, which the
// engine resolves at INSERT (field_defaults): `spaces` (a blank — seeding the
// word overflows a CHAR(1) status, ORA-12899) and, on a date column, `today` /
// `sysdate` / … (the word is not a date).
const BARE_DATE_KEYWORDS = new Set([
  'today', 'current_date', 'now', 'sysdate', 'systimestamp', 'getdate', 'current_timestamp',
])
function isEngineFilledKeyword(col: ColumnDef, dv: string): boolean {
  const word = dv.trim().toLowerCase()
  if (word === 'spaces') return true
  const ct = (col.col_type || '').toString().toUpperCase()
  return ['DATE', 'DATETIME', 'TIMESTAMP', 'TIME'].includes(ct) && BARE_DATE_KEYWORDS.has(word)
}

// Visible columns ordered for grid/list rendering: by ``x`` (the
// designer's left-to-right grid position), with array index as a
// stable tiebreaker so unset/equal x values fall back to authoring
// order. Used by ListView / ChildFormTab so the live preview grid
// matches the order the author saw in the designer.
export function gridOrderedColumns(sm: SqlModel): ColumnDef[] {
  return xOrderedColumns(sm, (c) => !isHidden(c))
}

// The read-only LIST grid (main list, a child form's row list): as
// gridOrderedColumns, but visibility is ``list_hidden``'s where set. The
// editable entry grids keep gridOrderedColumns — a column the form hides must
// not become editable there.
export function listOrderedColumns(sm: SqlModel): ColumnDef[] {
  return xOrderedColumns(sm, (c) => !isListHidden(c))
}

// Every column either grid can show — what "Apply layout" renumbers, so
// baking one grid's order never leaves the other's columns without an x.
export function layoutOrderedColumns(sm: SqlModel): ColumnDef[] {
  return xOrderedColumns(sm, (c) => !isHidden(c) || !isListHidden(c))
}

function xOrderedColumns(sm: SqlModel, keep: (c: ColumnDef) => boolean): ColumnDef[] {
  const wraps = sm.columns || []
  const visible: Array<{ col: ColumnDef; index: number }> = []
  wraps.forEach((w, i) => {
    if (!w?.column || !keep(w.column)) return
    visible.push({ col: w.column, index: i })
  })
  visible.sort((a, b) => {
    const dx = (Number(a.col.x) || 0) - (Number(b.col.x) || 0)
    if (dx !== 0) return dx
    return a.index - b.index
  })
  return visible.map(v => v.col)
}

export interface ParsedApiError {
  message: string
  validationErrors: ValidationErrorItem[]
  code: string | null
  /** Full error envelope details (traceback, cause, etc.) when the
   *  server attached them — used to build a copyable error report. */
  details: Record<string, unknown> | null
  /** HTTP status code, when available. */
  status: number | null
}

export function parseApiError(err: unknown): ParsedApiError {
  // Axios wraps server errors as err.response.data
  const anyErr = err as {
    response?: { data?: ErrorEnvelope | { detail?: unknown }; status?: number }
    message?: string
  }
  const env = anyErr?.response?.data
  const status = anyErr?.response?.status ?? null
  if (env && typeof env === 'object') {
    // Twasta envelope: {"error": {"code": "...", "message": "..."}}
    if ((env as ErrorEnvelope).error) {
      const e = (env as ErrorEnvelope).error
      return {
        message: e.message || 'Unknown error',
        validationErrors: e.details?.errors || [],
        code: e.code || null,
        details: e.details ?? null,
        status,
      }
    }
    // FastAPI HTTPException: {"detail": "..."}
    const detail = (env as { detail?: unknown }).detail
    if (typeof detail === 'string' && detail.trim()) {
      return { message: detail, validationErrors: [], code: null, details: null, status }
    }
  }
  return {
    message: anyErr?.message || 'Unknown error',
    validationErrors: [],
    code: null,
    details: null,
    status,
  }
}

// Assemble a single copy-paste-ready block of text from a failed
// live-preview request, framed as a prompt an AI coding agent can act
// on directly. It opens with an explicit ask, then lays out the
// operation context, HTTP status, error code, message, any validation
// errors, and — when the backend attached it — the full server-side
// traceback. This is what the "Copy" button on the error modal puts on
// the clipboard so the author can paste it straight to an agent.
export function formatErrorReport(context: string, parsed: ParsedApiError): string {
  const d = parsed.details || {}
  const tb = typeof d.traceback === 'string' ? d.traceback : null
  const cause = typeof d.cause === 'string' ? d.cause : null

  const lines: string[] = []
  lines.push(
    'I hit the following error in the Twasta live preview while ' +
    `"${context.toLowerCase()}". Please find the root cause and fix it.`,
  )
  lines.push('')
  lines.push('## Error details')
  lines.push(`- Operation: ${context}`)
  if (parsed.status != null) lines.push(`- HTTP status: ${parsed.status}`)
  if (parsed.code) lines.push(`- Error code: ${parsed.code}`)
  lines.push(`- Message: ${parsed.message}`)
  if (cause && cause !== parsed.message) {
    lines.push(`- Underlying cause: ${cause}`)
  }

  if (parsed.validationErrors.length > 0) {
    lines.push('')
    lines.push('## Validation errors')
    for (const v of parsed.validationErrors) {
      lines.push(`- ${v.field || '(record)'}: ${v.message}`)
    }
  }

  if (tb) {
    lines.push('')
    lines.push('## Server traceback')
    lines.push('```')
    lines.push(tb.trimEnd())
    lines.push('```')
  }

  return lines.join('\n')
}

// Line Entry (`sql_model.line_entry`): the commands a line form offers —
// declared, implicit (one per source) and synthesised from
// `column.lookup_add_lines`. The twin of the engine's
// `metadata_loader.line_entry_of`.
export { lineCommandsOf, lineEntryOf } from './lineEntry/model'
